import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../../layout/main-layout';
import DuoPeriodPicker from '../../../components/ui/duo-period-picker';
import { APP_API_URL } from '../../../utils';
import formatNumber, {
  DUO_PERIOD_OPTIONS,
  datesForPeriod,
  defaultFilterDates,
  formatDate,
} from '../../../utils/functions';
import { getSaleNetRevenue, isBrokenTileSaleValidated } from '../../../utils/broken-tile-sale';

const PERIOD_THIS_YEAR = { label: 'Cette année', value: 'this_year' };
const PERIOD_CUSTOM = { label: '', value: '' };
const periodOptions = [...DUO_PERIOD_OPTIONS, PERIOD_THIS_YEAR];
const PER_PAGE = 10;
const PAYMENTS = { 1: 'Mobile Money', 2: 'Carte bancaire', 3: 'Virement', 4: 'Espèces', 5: 'Crédit', 6: 'Autre', 7: 'Chèques' };

const lineCost = (sale) => (sale.items || []).reduce(
  (acc, item) => acc + (parseFloat(item.unit_cost) || 0) * (parseFloat(item.quantity) || 0),
  0
);

export default function DamagedGoodSalesList() {
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_CUSTOM);
  const [appliedFilters, setAppliedFilters] = useState(() => ({ search: '', ...defaultFilterDates() }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/damaged-goods/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fromDate: appliedFilters.dateFrom || undefined,
          toDate: appliedFilters.dateTo || undefined,
        },
      });
      setAllData(response.data?.sales || []);
    } catch (error) {
      console.error(error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters.dateFrom, appliedFilters.dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filtered = useMemo(() => allData.filter((item) => {
    const search = appliedFilters.search.trim().toLowerCase();
    if (!search) return true;
    return [item.customer?.name, item.note, item.user?.name].filter(Boolean).join(' ').toLowerCase().includes(search);
  }), [allData, appliedFilters.search]);

  const stats = useMemo(() => ({
    total: filtered.length,
    revenue: filtered.reduce((acc, item) => acc + getSaleNetRevenue(item), 0),
    cost: filtered.reduce((acc, item) => acc + lineCost(item), 0),
    validated: filtered.filter((item) => isBrokenTileSaleValidated(item)).length,
  }), [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const indexOfFirst = (currentPage - 1) * PER_PAGE;
  const currentItems = filtered.slice(indexOfFirst, indexOfFirst + PER_PAGE);

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Hors carrelage</p>
          <h1 className="duo-hero-title">Ventes articles cassés / gâtés</h1>
        </div>
        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Ventes</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Validées</p>
            <p className="duo-kpi-chip__value">{stats.validated}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Encaissé</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>{formatNumber(stats.revenue)} XOF</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Coût de revient</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>{formatNumber(stats.cost)} XOF</p>
          </div>
        </div>
        <DuoPeriodPicker
          options={periodOptions}
          selectedOption={selectedPeriod}
          onSelect={(option) => {
            const dates = datesForPeriod(option.value);
            setSelectedPeriod(option);
            setDraftFilters(dates);
            setAppliedFilters((prev) => ({ ...prev, ...dates }));
            setCurrentPage(1);
          }}
        />
        <section className="duo-panel mt-3">
          <div className="duo-toolbar">
            <span className="fw-bold">Liste des ventes</span>
            <div className="duo-toolbar__actions">
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--icon" onClick={fetchData}><i className="bi bi-arrow-clockwise" /></button>
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--icon" onClick={() => setFiltersOpen((open) => !open)}><i className="bi bi-funnel-fill" /></button>
              <Link to="/admin/damaged-goods/sales" className="duo-btn duo-btn--primary">Vendre</Link>
            </div>
          </div>
          <div className="px-3 pt-2">
            <input className="duo-input" type="search" placeholder="Client, note, vendeur…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          {filtersOpen && (
            <form
              className="duo-filters"
              onSubmit={(e) => {
                e.preventDefault();
                setSelectedPeriod(PERIOD_CUSTOM);
                setAppliedFilters((prev) => ({ ...prev, ...draftFilters }));
                setCurrentPage(1);
              }}
            >
              <p className="small text-muted">La plage de dates est celle qui est recherchée.</p>
              <div className="duo-filters__grid">
                <div>
                  <label className="duo-form-label">Date de début</label>
                  <input type="date" className="duo-input" value={draftFilters.dateFrom} onChange={(e) => { setSelectedPeriod(PERIOD_CUSTOM); setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value })); }} />
                </div>
                <div>
                  <label className="duo-form-label">Date de fin</label>
                  <input type="date" className="duo-input" value={draftFilters.dateTo} onChange={(e) => { setSelectedPeriod(PERIOD_CUSTOM); setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value })); }} />
                </div>
              </div>
              <div className="d-flex justify-content-end mt-2">
                <button type="submit" className="duo-btn duo-btn--primary">Appliquer</button>
              </div>
            </form>
          )}
          <div className="px-3 py-2 small text-muted">
            Plage : {appliedFilters.dateFrom} → {appliedFilters.dateTo}
            {selectedPeriod.label ? ` (${selectedPeriod.label})` : ''}
          </div>
          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Articles</th>
                  <th>Net</th>
                  <th>Paiement</th>
                  <th>Validation</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={7} className="duo-empty-row">Chargement des ventes…</td></tr>
                ) : currentItems.length > 0 ? currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="fw-bold">{item.customer?.name || '—'}</td>
                    <td>{(item.items || []).length}</td>
                    <td>{formatNumber(getSaleNetRevenue(item))} XOF</td>
                    <td>{PAYMENTS[item.payment_method] || item.payment_method}</td>
                    <td>{isBrokenTileSaleValidated(item) ? 'Validée' : 'Non validée'}</td>
                    <td>
                      <Link className="duo-btn duo-btn--primary duo-btn--sm" to={`/admin/damaged-goods/validate/${item.id}`}>
                        {isBrokenTileSaleValidated(item) ? 'Facture' : 'Valider'}
                      </Link>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="duo-empty-row">Aucune vente sur cette période.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(filtered.length > 0 || isLoading) && (
            <div className="duo-footer-bar">
              <span>{isLoading ? 'Chargement…' : `${indexOfFirst + 1} – ${Math.min(indexOfFirst + PER_PAGE, filtered.length)} sur ${filtered.length}`}</span>
              <ul className="duo-pagination">
                <li><button type="button" className="duo-btn duo-btn--ghost" disabled={isLoading || currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Précédent</button></li>
                <li><span className="duo-pagination__info px-2">Page {currentPage} / {totalPages}</span></li>
                <li><button type="button" className="duo-btn duo-btn--primary" disabled={isLoading || currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Suivant</button></li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
